import moment from 'moment';
import { Action } from 'redux';
import { IAppState, IQuoteCoverRequest, IUser, IUserQuote } from 'types';
import {
  getUserById,
  updateUser,
  deleteUserFromFamily,
  updateQuote,
  requestCover,
  uploadUserProfileAttachment,
  IFile,
} from 'services';
import {
  FB_EVENTS,
  getDeviceType,
  getGlobalIdTokenClaims,
  nullifyEmptyStrings,
  REDDIT_EVENTS,
  trackEvent,
} from 'helpers';
import { SubscriptionStatus, UserEmployment } from 'constants/user';
import queryString from 'helpers/queryString';

const LAST_ONBOARDING_STEP = 1;

export enum Types {
  GET_USER_BY_ID_LOADING = 'GET_USER_BY_ID_LOADING',
  GET_USER_BY_ID_LOADED = 'GET_USER_BY_ID_LOADED',
  GET_USER_BY_ID_FAILED = 'GET_USER_BY_ID_FAILED',
  UPDATE_USER_LOADING = 'UPDATE_USER_LOADING',
  UPDATE_USER_LOADED = 'UPDATE_USER_LOADED',
  UPDATE_USER_FAILED = 'UPDATE_USER_FAILED',
  SET_CURRENT_FAMILY = 'SET_CURRENT_FAMILY',
}

export interface IGetUserByIdLoading extends Action {
  type: Types.GET_USER_BY_ID_LOADING;
}

export interface IGetUserByIdLoaded extends Action {
  type: Types.GET_USER_BY_ID_LOADED;
  data: IUser;
}

export interface IGetUserByIdFailed extends Action {
  type: Types.GET_USER_BY_ID_FAILED;
  data: string;
}

export interface IUpdateUserLoading extends Action {
  type: Types.UPDATE_USER_LOADING;
}

export interface IUpdateUserLoaded extends Action {
  type: Types.UPDATE_USER_LOADED;
  data: IUser;
}

export interface ISetCurrentFamily extends Action {
  type: Types.SET_CURRENT_FAMILY;
  data?: string;
}

export const SetCurrentFamily = (data: string): ISetCurrentFamily => ({
  type: Types.SET_CURRENT_FAMILY,
  data,
});

export const GetUserByIdLoading = (): IGetUserByIdLoading => ({
  type: Types.GET_USER_BY_ID_LOADING,
});

export const GetUserByIdLoaded = (data: IUser): IGetUserByIdLoaded => ({
  type: Types.GET_USER_BY_ID_LOADED,
  data,
});

export const GetUserByIdFailed = (data: string = ''): IGetUserByIdFailed => ({
  type: Types.GET_USER_BY_ID_FAILED,
  data,
});

export const UpdateUserLoading = (): IUpdateUserLoading => ({
  type: Types.UPDATE_USER_LOADING,
});

export const UpdateUserLoaded = (data: IUser): IUpdateUserLoaded => ({
  type: Types.UPDATE_USER_LOADED,
  data,
});

export const GetUserByEmail = (user: Partial<IUser>) => async (dispatch) => {
  dispatch(GetUserByIdLoading());
  try {
    const result = await getUserById(null, user);
    const data: IUser = result.data.Items[0];
    dispatch(GetUserByIdLoaded(data));
  } catch {
    dispatch(GetUserByIdFailed('Email address is incorrect'));
  }
};

export const UpdateUser = (data: IUser) => async (dispatch) => {
  dispatch(UpdateUserLoading());
  const sanitized = nullifyEmptyStrings(data);
  dispatch(UpdateUserLoaded(sanitized));

  // Only call API when user ID exists
  if (sanitized.id) {
    await updateUser(sanitized); // Use await to ensure this completes, or handle promise appropriately
  }
};

export const GetLoggedInUser = () => async (dispatch) => {
  dispatch(GetUserByIdLoading());
  try {
    const result = await getUserById();
    const data: IUser = result.data.Items[0];

    const user = {
      ...data,
      lastUpdatedAt: moment.utc().format(),
    };

    if (user.newUser) {
      setTimeout(() => {
        const userClaims = getGlobalIdTokenClaims();

        if (userClaims) {
          const eventData = {
            userId: userClaims.sub,
            email: userClaims.email,
            value: '0.00',
            currency: 'USD',
            predicted_ltv: '0.00',
            deviceType: getDeviceType(),
          }

          if (eventData?.userId && eventData?.userId?.includes('facebook')) {
            eventData['fb_login_id'] = eventData.userId;
          }

          trackEvent(FB_EVENTS.Subscribe, eventData);

          trackEvent(REDDIT_EVENTS.SIGN_UP, {
            userId: userClaims.sub,
            deviceType: getDeviceType(),
          });
        }
      }, 1000); // GOOGLE ADS TRACKING => DO NOT REMOVE THIS EVENT!

      user.newUser = false;

      // Safely accessing window object
      const queryStringParams =
        typeof window !== 'undefined'
          ? queryString.parse(window.location.search)
          : {};
      if (queryStringParams.id && !data.employerId) {
        user.employerId = queryStringParams.id as string;
        user.employment = UserEmployment.EMPLOYED;
      }
      await updateUser(user); // Ensure update happens only once
    }

    dispatch(GetUserByIdLoaded(user));
  } catch {
    dispatch(GetUserByIdFailed());
  }
};

export const DeleteUserFromFamily =
  (userId: string, familyId: string) => () => {
    if (userId) {
      deleteUserFromFamily(userId, familyId);
    }
  };

export const UpdateUserOnboardingStep =
  (step = 0) =>
    (dispatch, getState) => {
      const { user }: IAppState = getState();
      if (user.id) {
        const updated = { ...user, onboardingStep: step + 1 };

        if (step >= LAST_ONBOARDING_STEP) {
          updated.walkthroughCompleted = true;
        }

        updateUser(updated);
        dispatch(UpdateUserLoaded(updated));
      }
    };

export const UpdateUserProfilePhoto =
  (file: IFile) => async (dispatch, getState) => {
    const { user }: IAppState = getState();
    const { data } = await uploadUserProfileAttachment(user, file);
    const updated = { ...user, picture: data.picture };
    dispatch(UpdateUserLoaded(updated));
  };

export const UpdateUserQuote = (quote: IUserQuote) => (dispatch, getState) => {
  const { user }: IAppState = getState();
  const quoteIdx = user.quotes.findIndex((q) => q.id === quote.id);
  if (quoteIdx >= 0) {
    updateQuote(quote);
    const updatedQuotes = [...user.quotes];
    updatedQuotes[quoteIdx] = quote;
    const updatedUser = { ...user, quotes: updatedQuotes };
    dispatch(UpdateUserLoaded(updatedUser));
  }
};

export const CreateUserQuote =
  (payload: IQuoteCoverRequest) => async (dispatch, getState) => {
    const { user }: IAppState = getState();
    const response = await requestCover(payload);
    const updatedQuotes = [response.data[1], ...user.quotes];
    const updatedUser = { ...user, quotes: updatedQuotes };
    dispatch(UpdateUserLoaded(updatedUser));
  };

export const ActivateSubscription = () => (dispatch, getState) => {
  const { user }: IAppState = getState();
  const updatedUser = {
    ...user,
    subscriptionStatus: SubscriptionStatus.ACTIVE,
  };
  dispatch(UpdateUserLoaded(updatedUser));
};
